import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '../../api'
import { TranslationMap } from '../../types/TranslationMap'

// Notice that in UI we call this a "Market Level Blueprint"
// Market Level Blueprints are global across all LiveOps tracked games
export interface MarketArchetype {
  id: string
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
  createdAt?: number
  modifiedAt?: number
}

export interface CreateArchetypeRequest {
  names: TranslationMap
  descriptions: TranslationMap
  discontinued: boolean
}

export const internalMarketsApi = createApi({
  reducerPath: 'internalMarketsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_CORE + '/i/',
    prepareHeaders: prepareDefaultHeaders,
  }),
  tagTypes: ['trackedArchetypes'],
  endpoints: (builder) => ({
    getTrackedArchetypes: builder.query<MarketArchetype[], void>({
      query: () => 'market/tracked/archetypes',
      providesTags: ['trackedArchetypes'],
    }),
    getTrackedArchetype: builder.query<MarketArchetype, string>({
      query: (archetypeId) => `market/tracked/archetypes/${archetypeId}`,
      providesTags: (result, error, archetypeId) => [{ type: 'trackedArchetypes', id: archetypeId }],
    }),
    createMarketArchetype: builder.mutation<MarketArchetype, CreateArchetypeRequest>({
      query: (body) => ({
        url: 'market/tracked/archetypes',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
    updateMarketArchetype: builder.mutation<
      MarketArchetype,
      { archetypeId: string; names: TranslationMap; descriptions: TranslationMap; discontinued: boolean }
    >({
      query: ({ archetypeId, names, descriptions, discontinued }) => ({
        url: `market/tracked/archetypes/${archetypeId}`,
        method: 'PUT',
        body: { names, descriptions, discontinued },
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
    deleteMarketArchetype: builder.mutation<void, string>({
      query: (archetypeId) => ({
        url: `market/tracked/archetypes/${archetypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['trackedArchetypes'],
    }),
  }),
})

export const {
  useGetTrackedArchetypesQuery,
  useGetTrackedArchetypeQuery,
  useCreateMarketArchetypeMutation,
  useUpdateMarketArchetypeMutation,
  useDeleteMarketArchetypeMutation,
} = internalMarketsApi
